<script setup>
import CompPagination from "@/components/CompPagination";
import IconsComp from "@/components/IconsComp";
import CompSearch from "@/components/CompSearch";
import CompCollapse from "@/components/CompCollapse";
import CompDropdown from "@/components/CompDropdown";
import CompBreadCrumbs from "@/components/CompBreadCrumbs";
import CompModal from "@/components/CompModal";
import CompSelect from "@/components/CompSelect";
import { CompAvaliation, CompDataLoad } from '@/components'
import { ref, onMounted, watch } from "vue";
import { useProposalStore, useReviewerStore, useUserStore } from "@/stores";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const reviewer_id = route.params.id;
const proposals = ref([]);
const proposalsCount = ref([])
const specialties = ref([])
const reviewer = ref(null);
const dataLoad = ref("Carregando..");
const currentPage = ref(1);
const oldCurrentPage = ref(1);
const firstPage = ref(1);
const lastPage = ref(1);
const perPage = ref(20);
const paginationOnLoad = ref(false)
const allStatus = ref(['Todos os status', 'Parecer pendente', 'Parecer enviado', 'Recusado', 'Prazo vencido']);
const selectedStatus = ref('Todos os status')
const selectedProposals = ref([])
const selectedAction = ref('')
const selectedSpecialty = ref(0)
const search = ref('')
const ArrowIcons = ref([]);
const deleteModalIsOpen = ref(false);
const activateUserModal = ref(false);
const deactivateUserModal = ref(false);
const statusModal = ref([])
const reviewRate = ref(0);

onMounted(async () => {
    reviewer.value = await useReviewerStore().find(reviewer_id).then(response => response?.data);

    if (!reviewer.value) {
        router.back();
        return;
    }

    await getProposals()
    proposalsCount.value = proposals.value

    specialties.value = reviewer.value?.especialidades ?? []
    specialties.value?.push({ id: 0, nome: "Todas especialidades" })
    specialties.value = specialties.value?.sort(obj => obj.id)
});

watch(currentPage, async (newValue, oldValue) => {
    if (newValue != oldValue) {
        await getProposals();
    }
});

watch(perPage, async (newValue, oldValue) => {
    if (newValue != oldValue) {
        await getProposals();
    }
});

async function getProposals() {
    proposals.value = [];
    dataLoad.value = "Carregando";

    paginationOnLoad.value = true

    let data = {
        nome: search.value,
        status: selectedStatus.value == 'Todos os status' ? '' : selectedStatus.value,
        especialidades: selectedSpecialty.value == 0 ? "" : selectedSpecialty.value,
        parecerista: reviewer_id,
        perPage: perPage.value,
        currentPage: currentPage.value !== oldCurrentPage.value ? currentPage.value : 1
    }

    proposals.value = await useReviewerStore().searchMyProposals(data)

    currentPage.value = proposals.value.meta.current_page;
    oldCurrentPage.value = currentPage.value
    perPage.value = proposals.value.meta.per_page;
    lastPage.value = proposals.value.meta.last_page;

    proposals.value = proposals.value.data;
    proposals.value.length === 0
        ? (dataLoad.value = "Nenhuma proposta encontrada")
        : "";

    paginationOnLoad.value = false

    proposals.value?.forEach((proposal, i) => {
        proposals.value[i].rate = reviewSum(proposal);
    })
}

function reviewSum(reviewer) {
    let answersInNumbers = null;

    if (reviewer?.avaliacao) {
        answersInNumbers = Object.entries(reviewer?.avaliacao)
            .filter((obj) => !isNaN(obj[1]))
            .map((obj) => obj[1])
            .map(Number);
    }

    if (answersInNumbers && !answersInNumbers.some(isNaN)) {
        return (
            answersInNumbers?.reduce((a, b) => {
                return a + b;
            }) / answersInNumbers?.length
        ).toFixed(1);
    }
    return 0.0
}

async function onDelete() {
    await useReviewerStore().delete(reviewer_id).then(() => {
        router.push({ name: "painel-inovahc-pareceristas" });
    });
}

async function activateUser() {
    await useUserStore().activate(reviewer_id).then(() => {
        router.push({ name: 'painel-inovahc-pareceristas' })
    })
}

async function deactivateUser() {
    await useUserStore().deactivate(reviewer_id).then(() => {
        router.push({ name: 'painel-inovahc-pareceristas' })
    })
}
</script>

<template>
    <section>
        <div class="container mx-auto px-6 pt-10 pb-4">
            <div class="flex justify-between flex-col md:flex-row items-center mb-8">
                <div class="hidden md:flex">
                    <CompBreadCrumbs>
                        <template v-slot:default>
                            <li>
                                <router-link :to="{ name: 'painel-inovahc-monitores' }">
                                    <IconsComp icon="user" class="w-6 h-6" />
                                    Usuários
                                </router-link>
                            </li>
                            <li>
                                <router-link :to="{ name: 'painel-inovahc-pareceristas' }">
                                    Pareceristas
                                </router-link>
                            </li>
                        </template>
                    </CompBreadCrumbs>
                </div>
                <div class="ml-auto flex">
                    <div>
                        <router-link :to="{ name: 'editar-parecerista', params: { id: reviewer_id } }"
                            class="btn primary no-underline">Editar</router-link>
                    </div>
                    <div class="ml-2">
                        <CompDropdown alignment="right">
                            <template #toggle>
                                <IconsComp icon="more" class="w-6 h-6 fill-green-500 rotate-90" />
                            </template>
                            <a v-if="reviewer?.inativo" @click.prevent="activateUserModal = !activateUserModal"
                                class="cursor-pointer">
                                <li class="text-purple-800">
                                    Ativar
                                    usuário
                                </li>
                            </a>
                            <a v-else @click.prevent="deactivateUserModal = !deactivateUserModal" class="cursor-pointer">
                                <li class="text-purple-800">
                                    Inativar
                                    usuário
                                </li>
                            </a>
                            <a @click.prevent="deleteModalIsOpen = !deleteModalIsOpen" class="cursor-pointer">
                                <li class="text-purple-800">
                                    Excluir
                                    usuário
                                </li>
                            </a>
                        </CompDropdown>
                    </div>

                    <CompModal v-model="activateUserModal" :isOpen="activateUserModal"
                        @close="activateUserModal = !activateUserModal" customClass="validacao">
                        <div class="text-xl font-poppins font-bold mb-2">
                            Confirmar ação
                        </div>
                        <div class="mb-8">
                            <p>
                                Tem certeza que deseja ativar o
                                <b>{{ reviewer?.nome_completo }}</b>?
                            </p>
                            <p>
                                Clique em “Ok” para deletar ou em “Cancelar” para permanecer na
                                página.
                            </p>
                        </div>
                        <div class="flex justify-end">
                            <button @click.prevent="activateUserModal = !activateUserModal" type="button"
                                class="btn secondary">
                                Cancelar
                            </button>
                            <button @click="activateUser
                                " type="button" class="btn primary">
                                Ok
                            </button>
                        </div>
                    </CompModal>

                    <CompModal v-model="deactivateUserModal" :isOpen="deactivateUserModal"
                        @close="deactivateUserModal = !deactivateUserModal" customClass="validacao">
                        <div class="text-xl font-poppins font-bold mb-2">
                            Confirmar ação
                        </div>
                        <div class="mb-8">
                            <p>
                                Tem certeza que deseja inativar o
                                <b>{{ reviewer?.nome_completo }}</b>?
                            </p>
                            <p>
                                Clique em “Ok” para deletar ou em “Cancelar” para permanecer na
                                página.
                            </p>
                        </div>
                        <div class="flex justify-end">
                            <button @click.prevent="deactivateUserModal = !deactivateUserModal" type="button"
                                class="btn secondary">
                                Cancelar
                            </button>
                            <button @click="deactivateUser
                                " type="button" class="btn primary">
                                Ok
                            </button>
                        </div>
                    </CompModal>

                    <CompModal v-model="deleteModalIsOpen" :isOpen="deleteModalIsOpen"
                        @close="deleteModalIsOpen = !deleteModalIsOpen" customClass="validacao">
                        <div class="text-xl font-poppins font-bold mb-2">
                            Confirmar ação
                        </div>
                        <div class="mb-8">
                            <p>
                                Tem certeza que deseja deletar o
                                <b>{{ reviewer?.nome_completo }}</b>?
                            </p>
                            <p>
                                Clique em “Ok” para deletar ou em “Cancelar” para permanecer na
                                página.
                            </p>
                        </div>
                        <div class="flex justify-end">
                            <button @click.prevent="deleteModalIsOpen = !deleteModalIsOpen" type="button"
                                class="btn secondary">
                                Cancelar
                            </button>
                            <button @click="onDelete
                                " type="button" class="btn primary">
                                Ok
                            </button>
                        </div>
                    </CompModal>
                </div>
            </div>

            <div class="flex flex-col lg:flex-row gap-6 mt-4">
                <div class="w-full lg:w-8/12">
                    <div class="flex">
                        <div class="rounded-full mr-4">
                            <img v-if="reviewer?.avatar" class="rounded-full max-w-[6em]" :src="reviewer?.avatar" alt="" />
                            <img v-else class="rounded-full max-w-[6em]" src="@/assets/img/template/profile2.png" alt="" />
                        </div>
                        <div class="grid grid-cols-1 gap-0">
                            <div class="text-5xl font-bold font-poppins text-purple-800">
                                {{ reviewer?.nome_completo }}
                            </div>
                            <div class="mt-2">
                                <p>{{ reviewer?.email }}</p>
                            </div>
                            <div class="text-purple-800">
                                <CompCollapse titulo="Experiência profissional" :collapseOpen="true"
                                    tituloClass="!text-green-500 !font-poppins !font-semibold !text-label-base">
                                    <div class="w-full">
                                        {{ reviewer?.experiencia_profissional }}
                                    </div>
                                </CompCollapse>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card w-full lg:w-4/12">
                    <div class="text-2xl font-bold font-poppins text-purple-800 mb-2">
                        Propostas
                    </div>
                    <div class="grid gap-6 lg:grid-cols-2 propostas">
                        <div class="big yellow">
                            <div class="title">
                                <IconsComp icon="proposta-filled" />
                                <span>
                                    {{ proposalsCount.filter(obj => obj?.status?.includes("Parecer pendente") &&
                                        $moment(obj?.data_prazo).isAfter($moment(), 'day')).length }}
                                </span>
                            </div>
                            <div class="description">Parecer pendente</div>
                        </div>

                        <div class="big green">
                            <div class="title">
                                <IconsComp icon="clock-filled" />
                                <span>
                                    {{ proposalsCount.filter(obj => obj?.status?.includes("Parecer enviado")).length }}
                                </span>
                            </div>
                            <div class="description">Propostas avaliadas</div>
                        </div>

                        <div class="small red">
                            <div class="title">
                                <IconsComp icon="timer-filled" />
                                <span>
                                    {{ proposalsCount.filter(obj => obj?.status?.includes("Prazo vencido") ||
                                        obj?.status?.includes("Parecer pendente") &&
                                        $moment(obj?.data_prazo).isBefore($moment(), 'day')).length }}
                                </span>
                            </div>
                            <div class="description">Prazo vencido</div>
                        </div>

                        <div class="small">
                            <div class="title">
                                <IconsComp icon="reject-filled" />
                                <span>
                                    {{ proposalsCount.filter(obj => obj?.status?.includes("Recusado")).length }}
                                </span>
                            </div>
                            <div class="description">Parecer recusado</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <!-- Filtro -->
    <section class="bg-purple-50 pt-8">
        <div v-if="selectedProposals.length === 0" class="container mx-auto px-6 pb-4">
            <div class="text-sm text-gray-400">Filtrar propostas</div>
            <div class="grid grid-cols-1 md:flex justify-between mt-4">
                <div class="flex items-center">
                    <div class="relative mr-2">
                        <CompSelect @change.prevent="getProposals()" class="max-w-sm" optionName="nome"
                            name="selectSpecialties" :options="specialties" v-model="selectedSpecialty"
                            :optionSelected="0" />
                    </div>
                    <div class="relative">
                        <CompSelect @change.prevent="getProposals()" class="max-w-sm" name="selectSpecialties"
                            :options="allStatus" v-model="selectedStatus" optionSelected="Todos os status " />
                    </div>
                </div>
                <div class="w-96 max-w-[100%]">
                    <CompSearch @submit.prevent="getProposals" name="search" v-model="search" />
                </div>
            </div>
        </div>
        <div v-else class="container mx-auto px-6 pb-4">
            <div class="text-sm text-gray-400">Ação em lote</div>
            <div class="flex items-center">
                <div class="mt-4">
                    <CompSelect v-model="selectedAction" name="action" optionDisabled="Selecione a ação" optionName="nome"
                        :options="[
                            { id: 1, nome: 'Opção 1' },
                            { id: 2, nome: 'Opção 2' },
                            { id: 3, nome: 'Opção 3' },
                        ]" />
                </div>
                <button class="btn primary ml-4" :disabled="!selectedAction">
                    Aplicar
                </button>
            </div>
        </div>
    </section>
    <!-- Tabela -->
    <section class="bg-purple-50">
        <div class="container mx-auto px-6 pt-2 pb-6">
            <div class="overflow-x-scroll md:overflow-hidden rounded-lg border border-white shadow-md">
                <table class="table">
                    <thead>
                        <tr>
                            <th scope="col">
                                <div class="flex items-center">
                                    Proposta
                                    <span>
                                        <IconsComp icon="filter-arrow-top" />
                                        <IconsComp icon="filter-arrow-down" />
                                    </span>
                                </div>
                            </th>
                            <th scope="col">
                                <div>
                                    Especialidade
                                    <span>
                                        <IconsComp icon="filter-arrow-top" />
                                        <IconsComp icon="filter-arrow-down" />
                                    </span>
                                </div>
                            </th>
                            <th scope="col">
                                <div>
                                    Inatividade
                                    <span>
                                        <IconsComp icon="filter-arrow-top" />
                                        <IconsComp icon="filter-arrow-down" />
                                    </span>
                                </div>
                            </th>
                            <th scope="col">
                                <div>
                                    Status
                                    <span>
                                        <IconsComp icon="filter-arrow-top" />
                                        <IconsComp icon="filter-arrow-down" />
                                    </span>
                                </div>
                            </th>
                            <th scope="col">
                                <div>
                                    Recebida em
                                    <span>
                                        <IconsComp icon="filter-arrow-top" />
                                        <IconsComp icon="filter-arrow-down" />
                                    </span>
                                </div>
                            </th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody v-show="proposals.length > 0" v-for="(tableValue, i) in proposals" :key="i">
                        <tr>
                            <td>
                                <div class="w-[240px] truncate flex items-center">
                                    {{ tableValue.proposta?.nome_proposta }}
                                </div>
                            </td>
                            <td>
                                <div class="w-32 truncate">
                                    {{
                                        tableValue.proposta?.especialidades?.map((obj) => obj.nome).join(", ")
                                    }}
                                </div>
                            </td>
                            <td>
                                <div>-</div>
                            </td>
                            <td>
                                <router-link v-if="!tableValue.status?.includes('Parecer enviado')"
                                    :to="{ name: 'painel-inovahc-proposta', params: { id: tableValue?.proposta?.id } }"
                                    class="no-underline">
                                    <button :class="tableValue.status.includes('Prazo vencido') || tableValue.status.includes('Parecer pendente') && $moment(tableValue.data_prazo).isBefore($moment(), 'day')
                                        ? 'tag red'
                                        : tableValue.status.includes('Recusado')
                                            ? 'tag disabled'
                                            : tableValue.status.includes('Parecer pendente')
                                                ? 'tag yellow'
                                                : tableValue.status.includes('Parecer enviado')
                                                    ? 'tag green'
                                                    : ''" type="button">
                                        {{ tableValue.status.includes('Parecer pendente') &&
                                            $moment(tableValue.data_prazo).isBefore($moment(), 'day') ? 'Prazo vencido' :
                                            tableValue.status }} </button>
                                </router-link>
                                <button v-else @click.prevent="statusModal[i] = !statusModal[i]" :class="tableValue.status.includes('Prazo vencido') || tableValue.status.includes('Parecer pendente') && $moment(tableValue.data_prazo).isBefore($moment(), 'day')
                                    ? 'tag red'
                                    : tableValue.status.includes('Recusado')
                                        ? 'tag disabled'
                                        : tableValue.status.includes('Parecer pendente')
                                            ? 'tag yellow'
                                            : tableValue.status.includes('Parecer enviado')
                                                ? 'tag green'
                                                : ''
                                    " type="button">
                                    {{ tableValue.status.includes('Parecer pendente') &&
                                        $moment(tableValue.data_prazo).isBefore($moment(), 'day') ? 'Prazo vencido' :
                                        tableValue.status }} </button>

                                <CompModal v-model="statusModal[i]" :isOpen="statusModal[i]"
                                    @close="statusModal[i] = !statusModal[i]" customClass="triagem">
                                    <button @click="statusModal[i] = !statusModal[i]" class="absolute right-5 top-5">
                                        <IconsComp icon="close-line" class="w-8 h-8 fill-green-500" />
                                    </button>
                                    <div>
                                        <div>
                                            <div class="flex items-center">
                                                <div class="text-purple-800 font-bold font-poppins text-xl">
                                                    Resultado da avaliação
                                                </div>
                                                <div class="text-purple-500 font-bold font-poppins text-xl ml-4">
                                                    {{ tableValue.rate }} pts
                                                </div>
                                            </div>
                                            <div
                                                class="mt-10 grid grid-cols-3 gap-4 items-end pb-8 border-b border-purple-500">
                                                <div>
                                                    <div class="text-green-500 mb-2">Diferencial de Inovação
                                                    </div>
                                                    <div class="text-purple-500">
                                                        {{
                                                            tableValue?.avaliacao?.innovation
                                                        }}
                                                        <span class="text-sm">pts</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="text-green-500 mb-2">
                                                        Viabilidade técnica e qualificação da equipe
                                                    </div>
                                                    <div class="text-purple-500">
                                                        {{
                                                            tableValue?.avaliacao?.viability
                                                        }}
                                                        <span class="text-sm">pts</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="text-green-500 mb-2">
                                                        Potencial de impacto social da solução
                                                    </div>
                                                    <div class="text-purple-500">
                                                        {{
                                                            tableValue?.avaliacao?.solution_potential
                                                        }}
                                                        <span class="text-sm">pts</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="text-green-500 mb-2">
                                                        Potencial de escala e sustentabilidade da solução
                                                    </div>
                                                    <div class="text-purple-500">
                                                        {{
                                                            tableValue?.avaliacao?.scale_potential
                                                        }}
                                                        <span class="text-sm">pts</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="text-green-500 mb-2">
                                                        Compatibilidade da solução com a proposta do programa
                                                    </div>
                                                    <div class="text-purple-500">
                                                        {{
                                                            tableValue?.avaliacao?.compatibility
                                                        }}
                                                        <span class="text-sm">pts</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="mt-8">
                                                <div class="text-green-500 text-lg mb-2">
                                                    Justificativa da nota
                                                </div>
                                                <div class="text-purple-500">
                                                    {{
                                                        tableValue?.avaliacao?.justify
                                                    }}
                                                </div>
                                                <div v-if="tableValue?.avaliacao?.challenges
                                                        ">
                                                    <div class="text-green-500 text-lg mt-4 mb-2">
                                                        Quais os desafios para viabilizar esta solução?
                                                    </div>
                                                    <div class="text-purple-500">
                                                        {{
                                                            tableValue?.avaliacao?.challenges
                                                        }}
                                                    </div>
                                                </div>
                                                <div v-if="tableValue?.avaliacao?.recomendations
                                                        ">
                                                    <div class="text-lg mt-4 mb-2">
                                                        Quais as recomendações e sugestões para viabilizar esta
                                                        solução?
                                                    </div>
                                                    <div class="text-purple-500 border-b border-purple-500 pb-4">
                                                        {{
                                                            tableValue?.avaliacao?.recomendations
                                                        }}
                                                    </div>
                                                </div>
                                                <div class="text-green-500 text-lg mt-4 mb-2">
                                                    Há conflito de interesses para a avaliação deste projeto?
                                                </div>
                                                <div class="text-purple-500">
                                                    {{
                                                        tableValue?.avaliacao?.has_conflict
                                                    }}
                                                    <p class="text-sm">
                                                        {{
                                                            tableValue?.avaliacao?.has_conflict_answer
                                                        }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CompModal>
                            </td>
                            <td>
                                <div>
                                    {{ $moment(tableValue.proposta?.created_at).format('DD/MM/YYYY') }}
                                </div>
                            </td>
                            <td>
                                <div class="flex items-center">
                                    <div v-if="tableValue.avaliacoes?.length > 0" class="ml-auto">
                                        <button @click.prevent="ArrowIcons[i] = !ArrowIcons[i]">
                                            <IconsComp :class="{ 'rotate-180': ArrowIcons[i] }" icon="arrow-down"
                                                class="transition-all duration-300 fill-green-500 w-6 h-6 ml-12 md:ml-4" />
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <CompAvaliation :avaliations="tableValue.avaliacoes" :isOpen="ArrowIcons[i]" />
                    </tbody>
                    <tbody v-if="proposals.length === 0">
                        <tr>
                            <td colspan="1000">
                                <CompDataLoad :dataLoad="dataLoad" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </section>
    <!--  Paginação -->
    <section class="bg-purple-50 pb-20 mb-0">
        <div class="container mx-auto px-6">
            <CompPagination :onLoad="paginationOnLoad" v-model:quantity="perPage" v-model:page="currentPage"
                :firstPage="firstPage" :lastPage="lastPage" />
        </div>
    </section>
</template>
